import { createApi } from "@reduxjs/toolkit/dist/query/react";
import baseQuery from "../../../../shared/data-access/store/baseQuery";
import {
  ConsumeNotificationsArg,
  EventArg,
  GetLeaderboardArg,
  Leaderboard,
  Login,
  LoginResponse,
  NotificationsResponse,
  OtpGenerateResponse,
  OtpLoginResponse,
  PointsResponse,
  RemindPassword,
  SubscribeDirect,
  SubscribePrompt,
  User,
  UserPUT,
} from "../../../../shared/interfaces/auth.types";

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery,
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, Login>({
      query: (credentials) => {
        return {
          url: "user/login",
          method: "POST",
          body: credentials,
        };
      },
      transformResponse: (response: LoginResponse) => {
        const token = response.apikey;
        localStorage.setItem("accessToken", token);
        return response;
      },
    }),
    loginWithToken: builder.mutation<LoginResponse, { token: string }>({
      query: (token) => {
        return {
          url: "user/login-via-token",
          method: "POST",
          body: token,
        };
      },
      transformResponse: (response: LoginResponse, meta, arg) => {
        const apikey = response.apikey;
        localStorage.setItem("tokenForLogin", arg.token);
        localStorage.setItem("accessToken", apikey);
        return response;
      },
    }),
    loginWithMsisdn: builder.mutation<LoginResponse, { msisdn: string }>({
      query: ({ msisdn }) => {
        return {
          url: "user/login-via-msisdn",
          method: "POST",
          body: {
            msisdn,
          },
        };
      },
      transformResponse: (response: LoginResponse) => {
        const token = response.apikey;
        localStorage.setItem("accessToken", token);
        return response;
      },
    }),
    remindPassword: builder.mutation<null, RemindPassword>({
      query: (msisdn) => {
        return {
          url: "user/remind-password",
          method: "POST",
          body: msisdn,
        };
      },
    }),
    subscribePrompt: builder.mutation<null, SubscribePrompt>({
      query: (msisdn) => {
        return {
          url: "user/subscribe-prompt",
          method: "POST",
          body: msisdn,
        };
      },
    }),
    generate: builder.mutation<null, SubscribePrompt>({
      query: (msisdn) => {
        return {
          // url: "user/generate",
          url: "user/otp-generate",
          method: "POST",
          body: msisdn,
        };
      },
    }),
    subscribeDirect: builder.mutation<
      { apikey: string; activationResult?: string; alreadyActive?: boolean },
      SubscribeDirect
    >({
      query: (pin) => {
        return {
          // url: "user/subscribe-direct",
          url: "user/otp-validate-and-activate",
          method: "POST",
          body: pin,
        };
      },
    }),
    unsubscribe: builder.mutation<null, void>({
      query: () => {
        return {
          url: "user/unsubscribe",
          method: "POST",
        };
      },
    }),
    logout: builder.mutation<null, void>({
      query: (token) => {
        return {
          url: "user/logout",
          method: "POST",
          body: token,
        };
      },
    }),
    getUser: builder.query<User, void>({
      query: () => "user/info",
    }),
    putUser: builder.mutation<User, UserPUT>({
      query: (settings) => {
        return {
          url: "user/info",
          method: "PUT",
          body: settings,
        };
      },
    }),
    getUserPoints: builder.query<PointsResponse, void>({
      query: () => "user/points",
    }),
    getLeaderboard: builder.query<Leaderboard, GetLeaderboardArg>({
      query: ({ limit, offset }) => {
        return {
          url: "user/leaderboard",
          params: { limit, offset },
        };
      },
    }),
    event: builder.mutation<null, EventArg>({
      query: (type) => {
        return {
          url: "user/event",
          method: "POST",
          body: type,
        };
      },
    }),
    getNotifications: builder.query<NotificationsResponse, void>({
      query: () => "user/notifications",
    }),
    consumeNotifications: builder.mutation<
      NotificationsResponse,
      ConsumeNotificationsArg
    >({
      query: (id) => {
        return {
          url: "user/notifications/consume",
          method: "POST",
          body: id,
        };
      },
    }),
    resolveHe: builder.mutation<{ heToken: string | null }, void>({
      query: () => ({
        url: "he/resolve",
        method: "POST",
      }),
    }),
    loginViaHe: builder.mutation<LoginResponse, { heToken: string }>({
      query: ({ heToken }) => {
        return {
          url: "user/login-via-he",
          method: "POST",
          body: {
            heToken,
          },
        };
      },
      transformResponse: (response: LoginResponse) => {
        const token = response.apikey;
        localStorage.setItem("accessToken", token);
        return response;
      },
    }),
    // Manual login via OTP — step 1: send the OTP SMS. Returns the `otpRef` handle
    // that identifies this attempt for the rest of the flow. For Resend, pass
    // `regenerate` together with the existing otpRef so the handle is preserved.
    otpGenerateForLogin: builder.mutation<
      OtpGenerateResponse,
      { msisdn: string; regenerate?: boolean; otpRef?: string }
    >({
      query: ({ msisdn, regenerate, otpRef }) => {
        return {
          url: "user/otp-login-generate",
          method: "POST",
          body: { msisdn, regenerate, otpRef },
        };
      },
    }),
    // Manual login via OTP — step 2: submit the code.
    //
    // Two possible outcomes. When Upstream returns the ise2 on the validate
    // response the server completes the login and answers with an apikey. When it
    // does not, the ise2 is coming on the Curry notification instead: the server
    // answers `pending` and the client polls otpLoginStatus.
    otpValidateLogin: builder.mutation<
      OtpLoginResponse,
      { msisdn: string; otp: string; otpRef?: string }
    >({
      query: ({ msisdn, otp, otpRef }) => {
        return {
          url: "user/otp-login",
          method: "POST",
          body: { msisdn, otp, otpRef },
        };
      },
      transformResponse: (response: OtpLoginResponse) => {
        // Only on the synchronous outcome — a pending response has no token yet.
        if (response.apikey) {
          localStorage.setItem("accessToken", response.apikey);
        }
        return response;
      },
    }),
    // Manual login via OTP — step 3, only when step 2 answered `pending`: poll
    // until the Curry notification resolves the attempt.
    otpLoginStatus: builder.mutation<OtpLoginResponse, { otpRef: string }>({
      query: ({ otpRef }) => {
        return {
          url: "user/otp-login/status",
          method: "POST",
          body: { otpRef },
        };
      },
      transformResponse: (response: OtpLoginResponse) => {
        if (response.apikey) {
          localStorage.setItem("accessToken", response.apikey);
        }
        return response;
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useRemindPasswordMutation,
  useSubscribePromptMutation,
  useGenerateMutation,
  useSubscribeDirectMutation,
  useUnsubscribeMutation,
  useLogoutMutation,
  useLoginWithTokenMutation,
  useLoginWithMsisdnMutation,
  useLazyGetUserQuery,
  useGetUserQuery,
  useGetUserPointsQuery,
  useGetLeaderboardQuery,
  usePutUserMutation,
  useConsumeNotificationsMutation,
  useEventMutation,
  useResolveHeMutation,
  useLoginViaHeMutation,
  useOtpGenerateForLoginMutation,
  useOtpValidateLoginMutation,
  useOtpLoginStatusMutation,
} = authApi;
